/**
 * Utilities for working with Firebase
 */

import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Get the current Firebase project ID from environment variables
 * with a fallback to the production project ID
 */
export const getFirebaseProjectId = (): string => {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 't1-mobile';
};

/**
 * Generate a Firebase Cloud Functions URL for a given function name
 * 
 * @param functionName - The name of the Firebase function
 * @param region - The Firebase function region (defaults to 'us-central1')
 * @returns The complete URL to the Firebase function
 */
export const getFirebaseFunctionUrl = (functionName: string, region: string = 'us-central1'): string => {
  const projectId = getFirebaseProjectId();
  return `https://${region}-${projectId}.cloudfunctions.net/${functionName}`;
};

/**
 * Log the current Firebase environment information
 * This is useful for debugging
 */
export const logFirebaseEnvironment = (): void => {
  const environment = process.env.REACT_APP_ENVIRONMENT || 'production';
  const projectId = getFirebaseProjectId();
  
  console.log(`Firebase Environment: ${environment}`);
  console.log(`Firebase Project ID: ${projectId}`);
};

/**
 * Updates the user's account status with the latest action
 * @param userId The user's ID
 * @param action The action performed ('added_account', 'deleted_account', 'strategy_subscribed', 'strategy_unsubscribed', 'trade_volume_changed')
 * @param accountId Optional account ID related to the action
 * @param strategyId Optional strategy ID related to the action
 * @param details Optional additional details about the action
 */
export const updateAccountStatus = async (
  userId: string,
  action: string,
  accountId?: string,
  strategyId?: string,
  details?: string
) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    
    // Create status object with only defined values
    const statusUpdate: any = {
      lastUpdated: Timestamp.now(),
      action
    };

    // Only add optional fields if they have values
    if (accountId) statusUpdate.accountId = accountId;
    if (strategyId) statusUpdate.strategyId = strategyId;
    if (details) statusUpdate.details = details;
    
    // Update the user document with the new status
    await updateDoc(userDocRef, {
      accountStatus: statusUpdate
    });
    
    console.log(`Updated account status for user ${userId}: ${action}`);
  } catch (error) {
    console.error('Error updating account status:', error);
  }
};